/**
 * Entity Type Utilities
 * 
 * Provides dynamic entity type handling based on backend notification definitions.
 * No hardcoded mappings - everything derived from backend data.
 */

import {AvailableNotification} from "../pages/Settings/types";

/**
 * Get human-readable label for an entity type
 * Uses backend entity_type and notification name/description to derive labels
 */
export function getEntityTypeLabel(entityType: string, notificationName?: string): string {
  // Use notification name if available, otherwise derive from entity_type
  if (notificationName) {
    return notificationName;
  }

  // Fallback: Convert snake_case or specific entity types to readable format
  const labelMap: Record<string, string> = {
    'strategy_map': 'Strategy Map',
    'strategic_map': 'Strategy Map',
    'work_item': 'Work Item',
    'sprint_report': 'Sprint',
    'sprint': 'Sprint',
    'task': 'Task Changes',
    'task_changes': 'Task Changes',
  };

  return labelMap[entityType] || entityType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get entity selection context (what entities to select)
 * Derives from entity_type what kind of entities user should select
 */
export function getEntitySelectionType(entityType: string): 'strategy_map' | 'work_item' | 'sprint' | 'task' | null {
  const typeMap: Record<string, 'strategy_map' | 'work_item' | 'sprint' | 'task' | null> = {
    'strategy_map': 'strategy_map',
    'strategic_map': 'strategy_map',
    'work_item': 'work_item',
    'sprint_report': 'sprint',
    'sprint': 'sprint',
    'task': 'task',
    'task_changes': 'task',
  };

  return typeMap[entityType] || null;
}

/**
 * Check if entity type requires entity selection
 * Some notifications (like task changes) don't need specific entity selection
 */
export function requiresEntitySelection(entityType: string): boolean {
  const selectionType = getEntitySelectionType(entityType);
  return selectionType !== 'task' && selectionType !== null;
}

/**
 * Get plural form of entity type label
 */
export function getEntityTypePluralLabel(entityType: string): string {
  const singularLabel = getEntityTypeLabel(entityType);
  
  // Handle special cases
  if (singularLabel.endsWith('y')) {
    return singularLabel.slice(0, -1) + 'ies';
  }
  
  return singularLabel + 's';
}

/**
 * Get selection prompt for entity type
 */
export function getSelectionPrompt(entityType: string): string {
  const selectionType = getEntitySelectionType(entityType);
  
  if (!selectionType || selectionType === 'task') {
    return 'Configure notification settings';
  }

  const label = getEntityTypePluralLabel(entityType);
  return `Select ${label}`;
}

/**
 * Build entity detail summary for display
 */
export function buildEntityDetailSummary(
  entityType: string,
  entityCount: number,
  additionalInfo?: { triggerCount?: number }
): string {
  const selectionType = getEntitySelectionType(entityType);
  const label = getEntityTypeLabel(entityType);
  const pluralLabel = getEntityTypePluralLabel(entityType);

  if (selectionType === 'task' && additionalInfo?.triggerCount) {
    const taskLabel = entityCount === 1 ? 'task' : 'tasks';
    const eventLabel = additionalInfo.triggerCount === 1 ? 'event' : 'events';
    return `${entityCount} ${taskLabel} • ${additionalInfo.triggerCount} ${eventLabel}`;
  }

  const displayLabel = entityCount === 1 ? label.toLowerCase() : pluralLabel.toLowerCase();
  return `${entityCount} ${displayLabel}`;
}

/**
 * Get backend entity type from notification
 * Returns the exact entity_type that should be sent to backend API
 */
export function getBackendEntityType(notification: AvailableNotification): string {
  return notification.entity_type;
}

/**
 * Check if notification supports frequency selection
 */
export function supportsFrequencySelection(notification: AvailableNotification): boolean {
  const frequencies = notification.metadata?.supports_frequencies || [];
  return frequencies.length > 1 || (frequencies.length === 1 && frequencies[0] !== 'immediate');
}

/**
 * Get available frequencies for notification
 */
export function getAvailableFrequencies(notification: AvailableNotification): string[] {
  const frequencies = notification.metadata?.supports_frequencies || [];
  return frequencies.length > 0 ? frequencies : [notification.default_frequency || 'daily'];
}

/**
 * Get delivery target types allowed for notification
 */
export function getAllowedTargetTypes(notification: AvailableNotification): Array<'channel' | 'user'> {
  // Check default_delivery_channels first
  const defaultChannels = notification.default_delivery_channels || [];
  if (defaultChannels.length > 0) {
    const types = new Set(
      defaultChannels.map(channel => channel.target_type === 'user' ? 'user' : 'channel')
    );
    return Array.from(types) as Array<'channel' | 'user'>;
  }

  // Fall back to delivery_scope
  const scope = notification.delivery_scope;
  if (scope === 'channel_or_user') {
    return ['channel', 'user'];
  } else if (scope === 'channel_only' || scope === 'channel') {
    return ['channel'];
  } else if (scope === 'user_only' || scope === 'user') {
    return ['user'];
  }

  // Default: allow both
  return ['channel', 'user'];
}
